import { Controller } from '@hotwired/stimulus';

import { ink, isValid, normalize } from '../utils/lot_color_contrast.js';

/**
 * color-picker — le champ « Couleur » d'un lot ou d'un jalon (`Form::ColorPickerComponent`, refonte planning
 * PR 2, Task 8.2 ; B02-01 … B02-14, RV-40, K-07, N-4).
 *
 * L'APERÇU est la SEULE exception bornée où le JS pose une couleur (K-07). Il n'écrit que trois variables sur
 * la RACINE du composant : `--lot-color` (toujours après `normalize` du jumeau `utils/lot_color_contrast.js`,
 * sinon la dernière valeur valide est gardée), `--lot-ink` (l'encre du MÊME jumeau, parité Ruby ↔ JS, sous le
 * voile comme les barres du Gantt, RV-41 ; la chaîne CSS vient de la valeur `inks` rendue par le serveur depuis
 * `Plans::LotColor::INK_CSS`) et `--picker-hue` (un nombre). Les dégradés de la zone « Personnalisée » sont
 * des `@utility` qui lisent `--picker-hue`. Jamais d'`innerHTML` (piège #591) : le nom et le code du
 * déclencheur fermé sont recopiés en `textContent` depuis les `data-color-*` que le serveur a posés sur chaque
 * radio.
 *
 * La SOUMISSION reste native : UN groupe de radios `phase[color]`. Le champ « Code couleur » n'a pas de
 * `name` : un code complet et valide devient la VALEUR de la radio « Personnalisée » (`radio.value = hex`),
 * qui s'active et se coche. Pendant la saisie, un préfixe plausible (`/^#?[0-9a-f]{0,6}$/i`) n'est pas une
 * erreur ; tout autre texte affiche « Code couleur invalide » en ligne. Tant que le code tapé porte la
 * sélection sans être valide, l'ENVOI DU FORMULAIRE EST BLOQUÉ (B02-12) et le panneau se rouvre sur l'erreur.
 * Vider le champ rend la main au choix d'avant la saisie (la radio re-cochée, l'aperçu recopié depuis elle) :
 * jamais d'écart entre l'aperçu et la valeur envoyée.
 *
 * Ouverture, Échap (capture : ferme le sélecteur, jamais le tiroir), clic extérieur et retour du focus :
 * le contrôleur partagé `disclosure-panel` (RV-36), sur la même racine. Ce contrôleur ne fait que lui
 * demander `open` / `close` par ses événements (`color-picker:open`, `color-picker:close`).
 *
 * Repli (P3-D11, 08/10) : tout « Personnalisée » vit dans un `<details>` natif « Couleur personnalisée… »
 * (cible `customDisclosure`, ouvert ou fermé par le NAVIGATEUR, état initial rendu par le serveur). Ses cibles
 * existent dès le rendu, replié ou non. Le contrôleur ne l'ouvre que dans deux cas : l'envoi bloqué (le champ
 * « Code couleur » doit être visible pour recevoir le focus) et une personnalisée adoptée hors du bloc (le
 * pré-remplissage du parent tape le code) ; il ne le referme jamais.
 *
 * Targets : name, code (déclencheur) ; customDisclosure, customRadio, codeInput, codeError, codeErrorIcon,
 *           hue, field (« Personnalisée »).
 * Values  : inks (Object, encre → chaîne CSS), saturation, brightness (Number, HSV de départ),
 *           invalidMessage (String).
 * Actions : select (change d'une radio), choose (Entrée sur une radio : ferme sans soumettre le tiroir),
 *           typeCode (saisie du code), commitCode (Entrée dans le code), hue (curseur), pick (zone).
 *
 * @example
 *   <div data-controller="color-picker disclosure-panel"
 *        data-action="color-picker:close->disclosure-panel#close color-picker:open->disclosure-panel#open"
 *        style="--lot-color: #e8b04b; --lot-ink: var(--color-neutral-900); --picker-hue: 39">…</div>
 */
const PREFIX = /^#?[0-9a-f]{0,6}$/i;

export default class extends Controller {
  static targets = [
    'name',
    'code',
    'customDisclosure',
    'customRadio',
    'codeInput',
    'codeError',
    'codeErrorIcon',
    'hue',
    'field',
  ];

  static values = {
    inks: Object,
    saturation: Number,
    brightness: Number,
    invalidMessage: String,
  };

  connect() {
    this.typedOwnsSelection = false;
    // Le choix que vider « Code couleur » rétablit : la radio cochée au rendu, puis chaque radio choisie,
    // « Personnalisée » comprise avec sa valeur du moment (bug du 03/10 : sinon, plus aucune radio cochée,
    // rien de posté, aperçu faux ; revue du 03/10 : une « Personnalisée » re-cliquée rendait la main à la
    // radio d'avant elle).
    this.chosen = this.#snapshot(this.element.querySelector('input[type="radio"]:checked'));
    this.form = this.element.closest('form');
    this.boundSubmitGuard = this.submitGuard.bind(this);
    this.form?.addEventListener('submit', this.boundSubmitGuard);
  }

  disconnect() {
    this.form?.removeEventListener('submit', this.boundSubmitGuard);
  }

  // Une radio choisie (clic, flèches, Espace) : l'aperçu, le déclencheur, et le code tapé n'a plus la main.
  select(event) {
    const radio = event.currentTarget;
    if (!radio.checked) return;

    this.typedOwnsSelection = radio === this.customRadioTarget;
    this.chosen = this.#snapshot(radio);
    this.#setInvalid(false);
    this.#preview(radio.dataset.colorHex);
    this.#describe(radio.dataset.colorName || '', radio.dataset.colorCode || '');
  }

  // Un CLIC de pointeur sur une pastille ferme le panneau, comme le choix d'une liste déroulante (Nicolas,
  // 16/10 : le panneau flotte désormais par-dessus le formulaire). Jamais au clavier : les flèches d'un
  // groupe de radios émettent aussi `click`, mais avec `detail === 0`, et chaque flèche fermerait le panneau.
  closeOnPointerPick(event) {
    if (event.detail > 0) this.dispatch('close');
  }

  // Entrée sur une radio : sinon elle soumettrait le formulaire du tiroir (SPEC-SILENT Q12).
  choose(event) {
    event.preventDefault();
    this.dispatch('close');
  }

  typeCode() {
    const raw = this.codeInputTarget.value.trim();
    this.typedOwnsSelection = raw !== '';
    if (raw === '') {
      this.#setInvalid(false);
      this.#restoreChosen();
      return;
    }

    const hex = normalize(raw.startsWith('#') ? raw : `#${raw}`);
    if (isValid(hex)) {
      this.#adoptCustom(hex);
      return;
    }

    this.#releaseCustom();
    this.#setInvalid(!PREFIX.test(raw));
  }

  // Entrée dans le champ « Code couleur » : jamais une soumission du tiroir ; un code invalide s'affiche.
  commitCode(event) {
    event.preventDefault();
    if (this.#typedInvalid()) {
      this.#setInvalid(true);
      return;
    }
    this.dispatch('close');
  }

  hue() {
    const degrees = Number(this.hueTarget.value) || 0;
    this.element.style.setProperty('--picker-hue', String(degrees));
    this.#adoptCustom(this.#hsvHex(degrees, this.saturationValue, this.brightnessValue));
    this.chosen = this.#snapshot(this.customRadioTarget);
  }

  // Clic (ou appui) dans la zone saturation / luminosité.
  pick(event) {
    const box = this.fieldTarget.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return;

    this.saturationValue = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
    this.brightnessValue = 1 - Math.min(Math.max((event.clientY - box.top) / box.height, 0), 1);
    this.#adoptCustom(
      this.#hsvHex(Number(this.hueTarget.value) || 0, this.saturationValue, this.brightnessValue)
    );
    // Un choix dans la zone « Personnalisée » est un choix explicite : vider ensuite le code le restaure.
    this.chosen = this.#snapshot(this.customRadioTarget);
  }

  // B02-12 : un code tapé qui porte la sélection sans être valide bloque l'envoi, erreur en ligne.
  submitGuard(event) {
    if (!this.#typedInvalid()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#setInvalid(true);
    this.dispatch('open');
    this.#revealCustom();
    this.codeInputTarget.focus();
  }

  #typedInvalid() {
    if (!this.typedOwnsSelection || !this.hasCodeInputTarget) return false;

    const raw = this.codeInputTarget.value.trim();
    return raw !== '' && !isValid(normalize(raw.startsWith('#') ? raw : `#${raw}`));
  }

  // Le code valide devient la VALEUR de la radio « Personnalisée », qui s'active et se coche.
  #adoptCustom(hex) {
    const radio = this.customRadioTarget;
    radio.value = hex;
    radio.dataset.colorHex = hex;
    radio.dataset.colorCode = hex.toUpperCase();
    radio.disabled = false;
    radio.checked = true;
    this.typedOwnsSelection = true;
    this.#revealCustom();
    if (document.activeElement !== this.codeInputTarget)
      this.codeInputTarget.value = hex.toUpperCase();
    this.#setInvalid(false);
    this.#preview(hex);
    this.#describe('', hex.toUpperCase());
  }

  // Déplie « Couleur personnalisée… » : un champ d'un `<details>` fermé n'est ni visible ni focalisable.
  #revealCustom() {
    if (this.hasCustomDisclosureTarget) this.customDisclosureTarget.open = true;
  }

  // Un code incomplet ne porte plus de valeur soumise : la radio « Personnalisée » se désactive.
  #releaseCustom() {
    const radio = this.customRadioTarget;
    radio.checked = false;
    radio.disabled = true;
  }

  // Champ vidé : la radio choisie avant la saisie est re-cochée (une « Personnalisée » rendue par le serveur
  // retrouve SA valeur), l'aperçu et le déclencheur sont recopiés depuis elle. Sans choix connu, la
  // « Personnalisée » se désactive comme pour un code incomplet.
  #restoreChosen() {
    const chosen = this.chosen;
    if (!chosen) {
      this.#releaseCustom();
      return;
    }

    const { radio } = chosen;
    if (radio === this.customRadioTarget) {
      radio.value = chosen.value;
      radio.dataset.colorHex = chosen.hex;
      radio.dataset.colorCode = chosen.code;
      radio.disabled = false;
    }
    radio.checked = true;
    this.#preview(radio.dataset.colorHex);
    this.#describe(radio.dataset.colorName || '', radio.dataset.colorCode || '');
  }

  #snapshot(radio) {
    if (!radio) return null;

    return {
      radio,
      value: radio.value,
      hex: radio.dataset.colorHex,
      code: radio.dataset.colorCode,
    };
  }

  // Le message en `textContent` (jamais d'innerHTML) ; l'icône du message suit par l'ATTRIBUT `hidden`.
  #setInvalid(invalid) {
    if (!this.hasCodeInputTarget) return;

    this.codeInputTarget.toggleAttribute('aria-invalid', invalid);
    if (invalid) this.codeInputTarget.setAttribute('aria-invalid', 'true');
    if (this.hasCodeErrorTarget)
      this.codeErrorTarget.textContent = invalid ? this.invalidMessageValue : '';
    if (this.hasCodeErrorIconTarget) this.codeErrorIconTarget.hidden = !invalid;
  }

  // L'aperçu : la couleur normalisée et validée, son encre calculée par le jumeau (sous le voile, RV-41).
  #preview(raw) {
    const hex = normalize(raw);
    if (!isValid(hex)) return;

    this.element.style.setProperty('--lot-color', hex);
    this.element.style.setProperty('--lot-ink', this.inksValue[ink(hex, { veil: true })] || '');
  }

  #describe(name, code) {
    if (this.hasNameTarget) this.nameTarget.textContent = name;
    if (this.hasCodeTarget) this.codeTarget.textContent = code;
  }

  // HSV → `#rrggbb` (une VALEUR, normalisée et validée par `#preview` avant toute écriture de style).
  #hsvHex(hueDegrees, saturation, brightness) {
    const chroma = brightness * saturation;
    const sector = (hueDegrees % 360) / 60;
    const second = chroma * (1 - Math.abs((sector % 2) - 1));
    const [r, g, b] = [
      [chroma, second, 0],
      [second, chroma, 0],
      [0, chroma, second],
      [0, second, chroma],
      [second, 0, chroma],
      [chroma, 0, second],
    ][Math.floor(sector) % 6];
    const offset = brightness - chroma;
    const channel = (value) =>
      Math.round((value + offset) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${channel(r)}${channel(g)}${channel(b)}`;
  }
}
